/**
 * Turning a generated lens into a capability view, without carrying its
 * controls — or the partition that reads them — along.
 *
 * A capability view — graph, plot, document, calendar, timeline — is built by
 * keeping a generated spec's target and tokens and REPLACING its root. Every
 * one of the five did that with a plain spread, and a spread copies
 * `viewState`.
 *
 * That is a real defect rather than a tidiness one, because the renderer draws
 * the controls from `ir.viewState` and nothing else: `render-html.ts` emits the
 * `.m-viewstate` group above `node(ir.root)` whatever the root turns out to be.
 * So a lens that had earned a view-state slot, switched to Graph, drew a date
 * filter above a force layout — and the predicates that slot fed went out with
 * the regions it replaced, so moving it changed nothing at all. **An affordance
 * with nothing behind it is worse than a missing one** is the sentence this
 * repo's relation tests open with; this is the same sentence about a filter.
 *
 * Raised by @Codex reviewing #137.
 *
 * The key is DELETED rather than set to `undefined`, because the workspace runs
 * `exactOptionalPropertyTypes` and the two are not the same value — absent
 * means "this lens has no controls", present-and-undefined is a shape the
 * schema does not describe.
 */

import type { TLayoutSpec } from '@morph/spec';

export function withoutViewState(spec: TLayoutSpec): TLayoutSpec {
  const carried = spec as TLayoutSpec & { partition?: unknown };
  if (carried.viewState === undefined && carried.partition === undefined) return spec;
  // BOTH, because they are one declaration. `partition` names the slot by index
  // and its branches by node id, and a capability view replaces the root — so a
  // spread that kept it left a spec pointing at a control that no longer exists
  // and at regions that no longer exist. `validate.ts` says so in two codes at
  // once: `partition-reads-undeclared-slot, partition-branch-is-not-a-node`,
  // and the shell refuses the whole lens, so Calendar rendered an error box
  // instead of a calendar.
  //
  // Caught by `e2e/calendar.mjs` in the gate, not here: the unit tests and
  // `e2e/partition.mjs` both pass, because neither drives a capability view. The
  // reasoning at the top of this file already covered it — *"the predicates
  // that slot fed went out with the regions it replaced"* — and I added a
  // second field to the same declaration without re-reading the paragraph
  // written about the first.
  const { viewState: _slots, partition: _partition, ...rest } = carried;
  return rest as TLayoutSpec;
}
